'use client';

import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import {
  analyzeInterference,
  type InterferenceAnalysisConfig,
  type InterferenceAnalysisResponse,
} from '@/lib/urm-api';

export const DEFAULT_INTERFERENCE_CONFIG: InterferenceAnalysisConfig = {
  minimum_p95_requests: 20,
  allow_mean_latency_fallback: true,
  degradation_z: 3,
  minimum_latency_rise: 0.25,
  minimum_latency_rise_ms: 25,
  controls_per_event: 4,
};

export type AnalysisPhase = 'idle' | 'workload' | 'ratelimit' | 'analyzing' | 'complete' | 'cancelled' | 'error';

export interface AnalysisProgress {
  phase: AnalysisPhase;
  workloadPages: number;
  workloadIntervals: number;
  ratelimitPages: number;
  ratelimitEntries: number;
  analysisStartedAtMs: number | null;
  analysisEstimatedMs: number;
}

interface CachedAnalysisInput {
  fromMs: number;
  toMs: number;
}

function abortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function estimateAnalysisDurationMs(workloadIntervals: number, ratelimitEntries: number): number {
  return Math.round(Math.min(180_000, Math.max(8_000, 5_000 + workloadIntervals * 6 + ratelimitEntries * 0.08)));
}

export function useInterferenceAnalysis(fromMs: number, toMs: number) {
  const [result, setResult] = useState<InterferenceAnalysisResponse | null>(null);
  const [progress, setProgress] = useState<AnalysisProgress>({
    phase: 'idle', workloadPages: 0, workloadIntervals: 0, ratelimitPages: 0, ratelimitEntries: 0,
    analysisStartedAtMs: null, analysisEstimatedMs: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const runRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const cachedRef = useRef<CachedAnalysisInput | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const run = ++runRef.current;
    const current = () => runRef.current === run && !controller.signal.aborted;
    setLoading(true);
    setRecalculating(false);
    setError(null);
    setEmpty(false);
    setResult(null);
    setProgress({
      phase: 'analyzing', workloadPages: 0, workloadIntervals: 0, ratelimitPages: 0, ratelimitEntries: 0,
      analysisStartedAtMs: Date.now(), analysisEstimatedMs: estimateAnalysisDurationMs(Math.ceil((toMs - fromMs) / 30_000), 0),
    });

    try {
      const analysis = await analyzeInterference({
        from_ms: fromMs,
        to_ms: toMs,
        config: DEFAULT_INTERFERENCE_CONFIG,
      }, controller.signal);
      if (!current()) return;
      if (analysis.workload_interval_count === 0) {
        cachedRef.current = null;
        setEmpty(true);
        setProgress((value) => ({ ...value, phase: 'complete' }));
        return;
      }
      cachedRef.current = { fromMs, toMs };
      setResult(analysis);
      setLoadedAt(Date.now());
      setProgress((value) => ({
        ...value, phase: 'complete', workloadIntervals: analysis.workload_interval_count, ratelimitEntries: analysis.ratelimit_entry_count,
      }));
    } catch (caught) {
      if (!current() || abortError(caught)) return;
      setError(errorMessage(caught));
      setProgress((value) => ({ ...value, phase: 'error' }));
    } finally {
      if (current()) {
        setLoading(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    }
  }, [fromMs, toMs]);

  useEffect(() => {
    startTransition(() => {
      runRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
      cachedRef.current = null;
      setResult(null);
      setError(null);
      setEmpty(false);
      setLoading(false);
      setRecalculating(false);
      setLoadedAt(null);
      setProgress({
        phase: 'idle', workloadPages: 0, workloadIntervals: 0, ratelimitPages: 0, ratelimitEntries: 0,
        analysisStartedAtMs: null, analysisEstimatedMs: 0,
      });
    });
  }, [fromMs, toMs]);

  useEffect(() => {
    return () => {
      runRef.current += 1;
      abortRef.current?.abort();
    };
  }, []);

  const recalculate = useCallback(async (candidateWeights: Record<string, number>) => {
    const cached = cachedRef.current;
    if (!cached) throw new Error('Load a metric range before recalculating weights');
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const run = ++runRef.current;
    setRecalculating(true);
    setError(null);
    setProgress((value) => ({
      ...value,
      phase: 'analyzing',
      analysisStartedAtMs: Date.now(),
      analysisEstimatedMs: estimateAnalysisDurationMs(Math.ceil((cached.toMs - cached.fromMs) / 30_000), 0),
    }));
    try {
      const analysis = await analyzeInterference({
        from_ms: cached.fromMs,
        to_ms: cached.toMs,
        candidate_weights: candidateWeights,
        config: DEFAULT_INTERFERENCE_CONFIG,
      }, controller.signal);
      if (runRef.current !== run || controller.signal.aborted) return;
      setResult(analysis);
      setProgress((value) => ({ ...value, phase: 'complete' }));
    } catch (caught) {
      if (runRef.current !== run || abortError(caught)) return;
      setError(errorMessage(caught));
      setProgress((value) => ({ ...value, phase: 'error' }));
      throw caught;
    } finally {
      if (runRef.current === run) {
        setRecalculating(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    }
  }, []);

  const cancel = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    abortRef.current = null;
    runRef.current += 1;
    setLoading(false);
    setRecalculating(false);
    setError(null);
    setProgress((value) => ({ ...value, phase: 'cancelled' }));
  }, []);

  return { result, progress, error, empty, loading, recalculating, loadedAt, active: loading || recalculating, refetch: load, recalculate, cancel };
}
